import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MarkdownContent from "@/components/MarkdownContent";
import MarkdownEditor from "@/components/MarkdownEditor";
import ReactionsBar from "@/components/ReactionsBar";
import AttachmentGrid from "@/components/AttachmentGrid";
import HideThreadModal from "@/components/HideThreadModal";
import {
  Pin, PinOff, Lock, Unlock, CheckCircle2, EyeOff, Eye,
  Flag, ChevronLeft, ShieldCheck, Trash2, MessageSquare,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

const ROLE_BADGE = {
  instructor: "bg-purple-500/15 text-purple-200 border-purple-500/40",
  staff_dcc: "bg-brand-orange-500/15 text-white/70 border-primary/40",
  superadmin_dcc: "bg-brand-orange-500/15 text-white/70 border-primary/40",
  alumno: "bg-gray-500/15 text-gray-300 border-gray-500/40",
};
const ROLE_LABEL = {
  instructor: "Instructor", staff_dcc: "Staff", superadmin_dcc: "Superadmin",
  alumno: "Alumno", partner_admin: "Partner",
};

export default function ForoTema() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [posting, setPosting] = useState(false);
  const [hidingTarget, setHidingTarget] = useState(null); // {target_type, target_id, currently_hidden}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/forums/threads/${threadId}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      if (e.response?.status === 404) nav(-1);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);

  if (loading || !data) {
    return (
      <AlumnoLayout user={user}>
        <div className="h-96 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />
      </AlumnoLayout>
    );
  }

  const t = data.thread;
  const isMod = data.my_permissions.is_moderator;
  const canEdit = data.my_permissions.can_edit_thread;
  const isLocked = t.is_locked;

  const submitReply = async (e) => {
    e.preventDefault();
    if (replying.trim().length < 1) { toast.error("Escribe una respuesta"); return; }
    setPosting(true);
    try {
      await api.post(`/forums/threads/${threadId}/posts`, {
        body_md: replying,
        attachments: replyAttachments.map((a) => a.id),
      });
      setReplying("");
      setReplyAttachments([]);
      toast.success("Respuesta publicada");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  const doLike = async (target, id, currentLiked) => {
    try {
      if (currentLiked) {
        await api.delete(`/forums/${target}s/${id}/like`);
      } else {
        await api.post(`/forums/${target}s/${id}/like`);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doReport = async (target, id) => {
    const reason = window.prompt("Motivo del reporte (opcional):") || "";
    try {
      await api.post(`/forums/${target}s/${id}/report`, { reason });
      toast.success("Reporte enviado a moderación");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const modAction = async (path, payload) => {
    try {
      await api.patch(path, payload);
      toast.success("Acción aplicada");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/forums/threads/${threadId}`);
      toast.success("Tema eliminado");
      nav(-1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const solutionPost = data.posts.find((p) => p.is_solution);
  const otherPosts = data.posts.filter((p) => !p.is_solution);

  return (
    <AlumnoLayout user={user}>
      <div>
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4" /> Volver al foro
        </button>

        {/* Thread */}
        <article
          className="p-6 bg-[#111827] border border-gray-800 rounded-xl"
          data-testid="thread-detail"
        >
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {t.is_pinned && (
              <Chip color="emerald" icon={Pin}>Fijado</Chip>
            )}
            {t.is_locked && (
              <Chip color="gray" icon={Lock}>Bloqueado</Chip>
            )}
            {t.has_solution && (
              <Chip color="emerald" icon={CheckCircle2}>Resuelto</Chip>
            )}
            {(t.tags || []).map((tag) => (
              <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-gray-700 text-gray-300">
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-2xl font-bold text-white font-display" data-testid="thread-title">
            {t.title}
          </h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
            <span className="text-gray-300">{t.author_name}</span>
            <span className={`px-1.5 py-0.5 rounded border ${ROLE_BADGE[t.author_role] || ROLE_BADGE.alumno} uppercase tracking-widest font-semibold`}>
              {ROLE_LABEL[t.author_role] || t.author_role}
            </span>
            <span className="text-gray-500 font-mono">· {fmtDate(t.created_at)}</span>
          </div>

          <div className="mt-5 text-gray-200">
            <MarkdownContent>{t.body_md}</MarkdownContent>
          </div>
          {(t.attachments || []).length > 0 && (
            <AttachmentGrid attachments={t.attachments} />
          )}

          <div className="mt-6 flex flex-wrap items-center gap-2 pt-4 border-t border-gray-800">
            <ReactionsBar
              targetType="thread"
              targetId={t.id}
              summary={t.reactions_summary}
              myReaction={t.my_reaction}
              onChange={(summary, my_reaction) => setData((d) => ({
                ...d,
                thread: { ...d.thread, reactions_summary: summary, my_reaction },
              }))}
            />
            <button
              type="button"
              onClick={() => doReport("thread", t.id)}
              data-testid="thread-report-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-white/5 border border-gray-700 text-gray-300 hover:bg-white/10"
            >
              <Flag className="w-3.5 h-3.5" /> Reportar
            </button>
            {isMod && (
              <>
                <button
                  type="button"
                  onClick={() => modAction(`/forums/threads/${t.id}/pin`, { pinned: !t.is_pinned })}
                  data-testid="thread-pin-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-purple-500/15 border border-purple-500/40 text-purple-200 hover:bg-purple-500/25"
                >
                  {t.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                  {t.is_pinned ? "Desfijar" : "Fijar"}
                </button>
                <button
                  type="button"
                  onClick={() => modAction(`/forums/threads/${t.id}/lock`, { locked: !t.is_locked })}
                  data-testid="thread-lock-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-purple-500/15 border border-purple-500/40 text-purple-200 hover:bg-purple-500/25"
                >
                  {t.is_locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  {t.is_locked ? "Desbloquear" : "Bloquear"}
                </button>
                <button
                  type="button"
                  onClick={() => setHidingTarget({ target_type: "thread", target_id: t.id, currently_hidden: t.status === "hidden" })}
                  data-testid="thread-hide-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25"
                >
                  {t.status === "hidden" ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {t.status === "hidden" ? "Mostrar" : "Ocultar"}
                </button>
              </>
            )}
            {(canEdit || isMod) && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    data-testid="thread-delete-btn"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#111827] border-gray-800 text-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar este tema?</AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-400">
                      Se marcará como oculto (soft-delete). No se elimina de la base pero deja de mostrarse a los alumnos.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-200">Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white">
                      Sí, eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </article>

        {/* Solution first */}
        {solutionPost && (
          <section className="mt-6" data-testid="solution-section">
            <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold mb-2 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Respuesta marcada como solución
            </p>
            <PostCard
              post={solutionPost}
              isMod={isMod}
              onReport={() => doReport("post", solutionPost.id)}
              onModAction={modAction}
              onHideRequest={(id, hidden) => setHidingTarget({ target_type: "post", target_id: id, currently_hidden: hidden })}
              onReactionChange={(summary, my_reaction) => setData((d) => ({
                ...d,
                posts: d.posts.map((pp) => pp.id === solutionPost.id
                    ? { ...pp, reactions_summary: summary, my_reaction }
                    : pp),
              }))}
              highlighted
            />
          </section>
        )}

        {/* Other posts */}
        <section className="mt-6 space-y-3">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold flex items-center gap-1">
            <MessageSquare className="w-3.5 h-3.5" /> Respuestas · {data.total_posts}
          </p>
          {otherPosts.length === 0 && !solutionPost ? (
            <p className="text-sm text-gray-500 italic">Aún no hay respuestas. Sé el primero en aportar.</p>
          ) : (
            otherPosts.map((p) => (
              <PostCard
                key={p.id}
                post={p}
                isMod={isMod}
                onReport={() => doReport("post", p.id)}
                onModAction={modAction}
                onHideRequest={(id, hidden) => setHidingTarget({ target_type: "post", target_id: id, currently_hidden: hidden })}
                onReactionChange={(summary, my_reaction) => setData((d) => ({
                  ...d,
                  posts: d.posts.map((pp) => pp.id === p.id
                      ? { ...pp, reactions_summary: summary, my_reaction }
                      : pp),
                }))}
              />
            ))
          )}
        </section>

        {/* Reply form */}
        <section className="mt-8" data-testid="reply-section">
          {isLocked && !isMod ? (
            <div className="p-4 rounded-xl border border-gray-800 bg-[#111827] text-center">
              <Lock className="w-6 h-6 mx-auto text-gray-500" />
              <p className="mt-2 text-sm text-gray-400">Este tema está bloqueado. No se aceptan más respuestas.</p>
            </div>
          ) : (
            <form onSubmit={submitReply}>
              <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold mb-2">
                Tu respuesta
              </p>
              <MarkdownEditor
                value={replying}
                onChange={setReplying}
                rows={5}
                placeholder="Aporta tu punto de vista… (@ para mencionar)"
                testid="reply-body"
                attachments={replyAttachments}
                onAttachmentsChange={setReplyAttachments}
                mentionProgramSlug={t.program_slug}
              />
              <div className="mt-3 flex justify-end">
                <button
                  type="submit"
                  disabled={posting || replying.trim().length < 1}
                  data-testid="reply-submit-btn"
                  className="px-6 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold disabled:opacity-50"
                >
                  {posting ? "Publicando…" : "Publicar respuesta"}
                </button>
              </div>
            </form>
          )}
        </section>

        {hidingTarget && (
          <HideThreadModal
            targetType={hidingTarget.target_type}
            targetId={hidingTarget.target_id}
            currentlyHidden={hidingTarget.currently_hidden}
            onClose={() => setHidingTarget(null)}
            onDone={async () => { setHidingTarget(null); await load(); }}
          />
        )}
      </div>
    </AlumnoLayout>
  );
}

function PostCard({ post: p, isMod, onReport, onModAction, onHideRequest,
                     onReactionChange, highlighted }) {
  return (
    <article
      className={`p-5 rounded-xl border ${
        highlighted ? "border-emerald-500/50 bg-emerald-500/5" : "border-gray-800 bg-[#111827]"
      } ${p.status === "hidden" ? "opacity-60" : ""}`}
      data-testid={`post-${p.id}`}
      id={`p-${p.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap text-xs mb-3">
        <span className="text-gray-300 font-medium">{p.author_name}</span>
        <span className={`px-1.5 py-0.5 rounded border ${ROLE_BADGE[p.author_role] || ROLE_BADGE.alumno} uppercase tracking-widest font-semibold`}>
          {ROLE_LABEL[p.author_role] || p.author_role}
        </span>
        <span className="text-gray-500 font-mono">· {fmtDate(p.created_at)}</span>
        {p.status === "hidden" && (
          <span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-red-500/15 border border-red-500/40 text-red-300 uppercase tracking-widest font-semibold">
            Oculto
          </span>
        )}
        {p.status === "reported" && (
          <span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 uppercase tracking-widest font-semibold">
            Reportado
          </span>
        )}
      </div>
      <div className="text-gray-200">
        <MarkdownContent>{p.body_md}</MarkdownContent>
      </div>
      {(p.attachments || []).length > 0 && (
        <AttachmentGrid attachments={p.attachments} />
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <ReactionsBar
          targetType="post"
          targetId={p.id}
          summary={p.reactions_summary}
          myReaction={p.my_reaction}
          onChange={onReactionChange}
        />
        <button
          type="button"
          onClick={onReport}
          data-testid={`post-report-${p.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-white/5 border border-gray-700 text-gray-300 hover:bg-white/10"
        >
          <Flag className="w-3 h-3" /> Reportar
        </button>
        {isMod && (
          <>
            <button
              type="button"
              onClick={() => onModAction(`/forums/posts/${p.id}/mark-solution`, { is_solution: !p.is_solution })}
              data-testid={`post-solution-${p.id}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold ${
                p.is_solution
                  ? "bg-emerald-500/20 border border-emerald-500/50 text-emerald-200"
                  : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20"
              }`}
            >
              <CheckCircle2 className="w-3 h-3" /> {p.is_solution ? "Quitar solución" : "Marcar solución"}
            </button>
            <button
              type="button"
              onClick={() => onHideRequest(p.id, p.status === "hidden")}
              data-testid={`post-hide-${p.id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-500/10 border border-red-500/40 text-red-300 hover:bg-red-500/20"
            >
              {p.status === "hidden" ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {p.status === "hidden" ? "Mostrar" : "Ocultar"}
            </button>
          </>
        )}
      </div>
    </article>
  );
}

function Chip({ children, color, icon: Icon }) {
  const cls = {
    emerald: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
    gray: "bg-gray-500/15 border-gray-500/40 text-gray-300",
  }[color] || "bg-gray-500/15 border-gray-500/40 text-gray-300";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${cls} text-[10px] font-bold uppercase tracking-widest`}>
      {Icon && <Icon className="w-3 h-3" />} {children}
    </span>
  );
}
